import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useArtifacts, usePipelineStatus } from "@/hooks/useApi";
import { AGENT_DISPLAY_NAMES, AGENT_ICONS } from "@/lib/agent-ids";
import { ArtifactViewer } from "./ArtifactViewer";
import type { Artifact } from "@/types/api";
import { Landmark, Code, Database, TestTubeDiagonal, GraduationCap, ClipboardCheck, Calculator, ArrowRight } from "lucide-react";

const iconMap: Record<string, React.ElementType> = {
  ClipboardCheck, Landmark, Code, Database, TestTubeDiagonal, GraduationCap, Calculator,
};

interface ArtifactChainProps {
  projectId: string;
  agentId: string;
}

export function ArtifactChain({ projectId, agentId }: ArtifactChainProps) {
  const { t } = useTranslation();
  const { data: allArtifacts = [] } = useArtifacts(projectId);
  const { data: pipeline } = usePipelineStatus(projectId);
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null);

  // Only show artifacts from agents that are in the current pipeline
  // and that this agent depends on (via depends_on in PIPELINE_AGENTS).
  const pipelineAgent = pipeline?.agents?.find(a => a.agent_id === agentId);
  const dependsOn = new Set<string>(pipelineAgent?.depends_on || []);
  const seen = new Set<string>();
  const upstreamArtifacts = allArtifacts
    .filter(a => {
      // Must be from a different agent that's in the pipeline AND is a dependency
      if (a.agent_id === agentId) return false;
      if (a.artifact_type === "node_debug_log") return false;
      if (dependsOn.size > 0) return dependsOn.has(a.agent_id);
      // If no depends_on defined, don't show any upstream artifacts
      return false;
    })
    .filter(a => {
      const key = `${a.agent_id}:${a.artifact_type}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  if (upstreamArtifacts.length === 0) return null;

  return (
    <>
      <div className="rounded-lg border bg-card p-3">
        <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-2">
          {t("Upstream Artifacts")}
        </p>
        <div className="flex flex-wrap gap-2">
          {upstreamArtifacts.map((artifact) => {
            const iconName = AGENT_ICONS[artifact.agent_id] || "Landmark";
            const Icon = iconMap[iconName] || Landmark;
            const agentName = AGENT_DISPLAY_NAMES[artifact.agent_id] || artifact.agent_id;
            return (
              <button
                key={artifact.id}
                onClick={() => setSelectedArtifact(artifact)}
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs hover:border-primary/40 hover:bg-primary/5 transition-colors"
              >
                <Icon className="h-3 w-3 text-primary shrink-0" />
                <span className="font-medium">{artifact.title}</span>
                <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" />
                <Badge
                  variant="outline"
                  className="text-[9px] bg-success/10 text-success border-success/20"
                >
                  {agentName}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      <ArtifactViewer
        artifact={selectedArtifact}
        onClose={() => setSelectedArtifact(null)}
      />
    </>
  );
}
